import { Plus } from 'lucide-react'
import { type DragEvent, type ReactNode, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { formatDate } from '@/components/documents/format'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'
import { DEFAULT_FILE_ICON, FILE_ICONS, fileExtension } from '@/lib/fileIcon'
import { cn } from '@/lib/utils'
import { type NamedDocument, PARSE_PENDING } from '@/models/document'
import { type ComplianceSlot, type SlotAttachment, type SlotDocument, SlotStatus } from '@/models/vault'
import { SLOT_ACCEPT, SLOT_COLUMNS, SLOT_MAX_MB } from './slotColumns'
import { statusStyle } from './slotStatus'
import { SlotStatusPill } from './SlotStatusPill'

/** `replace` swaps out what the slot holds; `add` files another document beside it. */
export type UploadMode = 'add' | 'replace'

/** Asks one row to open its file picker — from the receipt banner's "still missing" buttons. */
export interface PickRequest {
  key: string
  nonce: number
}

export interface SlotRowActions {
  /** Opens the metadata drawer on one file of the document. */
  openDetails: (document: NamedDocument, attachmentId: string) => void
  upload: (slot: ComplianceSlot, file: File, mode: UploadMode) => void
  /** The slot an upload is in flight for, so its row can say so. */
  uploadingKey?: string | null
  pickRequest?: PickRequest | null
}

const BOX =
  'flex w-full flex-col gap-3 rounded-lg border bg-white px-4 py-3 text-left transition-colors lg:flex-row lg:items-center'

const ACCEPTED = new Set(SLOT_ACCEPT.split(','))

/** Why a file cannot be filed, or null when it can — the same rules the dropzone applies. */
function refusal(file: File): string | null {
  const extension = `.${fileExtension(file.name)}`.toLowerCase()
  if (!ACCEPTED.has(extension))
    return `That file type isn’t accepted. Use ${SLOT_ACCEPT.replaceAll(',', ', ')}.`
  if (file.size > SLOT_MAX_MB * 1024 * 1024) return `That file is over ${SLOT_MAX_MB} MB.`
  return null
}

function FileLink({ href, name }: { href: string | null; name: string }) {
  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="truncate text-sm font-medium text-blue-600 hover:underline"
    >
      {name}
    </a>
  ) : (
    <span className="text-muted-foreground truncate text-sm font-medium">{name}</span>
  )
}

interface FileRow {
  key: string
  document: SlotDocument
  href: string | null
  name: string
  extension: string
  status: SlotStatus
  dateExpiration: string | null
}

/** Every file on the slot's documents, newest document first, each with the dates read off it. */
function fileRows(documents: SlotDocument[]): FileRow[] {
  return documents.flatMap((document) => {
    const own: SlotAttachment[] = document.attachments.length
      ? document.attachments
      : [
          {
            id: document.id,
            displayName: document.displayName,
            file: document.file,
            fileName: document.fileName,
            fileSize: document.fileSize,
            dateExpiration: null,
            status: SlotStatus.VALID,
          },
        ]
    // One file reads as the document; several read as themselves.
    return own.map((attachment) => ({
      key: attachment.id,
      document,
      href: attachment.file,
      name: own.length === 1 ? document.displayName : attachment.displayName,
      extension: fileExtension(attachment.fileName ?? document.fileName),
      status: attachment.status,
      dateExpiration: attachment.dateExpiration,
    }))
  })
}

/** The slot's name and what it is for, with a file icon standing beside every line. */
function SlotHeading({
  slot,
  filled,
  icon: Icon = DEFAULT_FILE_ICON,
  children,
}: {
  slot: ComplianceSlot
  filled: boolean
  icon?: typeof DEFAULT_FILE_ICON
  /** What sits under the label — a single file's link, when there is one. */
  children?: ReactNode
}) {
  return (
    <div className={cn(SLOT_COLUMNS.document, 'flex gap-3', children ? 'items-start' : 'items-center')}>
      <Icon
        aria-hidden="true"
        strokeWidth={1.25}
        className={cn('size-9 shrink-0', filled ? 'text-muted-foreground' : 'text-ink-300')}
      />
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex min-w-0 items-center gap-2 leading-tight">
          <span className="truncate font-medium">{slot.label}</span>
          {!slot.required && <Badge variant="outline">Optional</Badge>}
        </div>
        {children}
        {slot.description && <span className="text-muted-foreground text-xs">{slot.description}</span>}
      </div>
    </div>
  )
}

/** A file that is there but earned nothing: the parser could not read it. */
function fileLabel(file: FileRow): string | undefined {
  return file.status === SlotStatus.MISSING ? "Couldn't read" : undefined
}

/** One file as a row of the card: its link, its status, its expiry, its details — the header's tracks. */
function AttachmentRow({
  file,
  isPending,
  onDetails,
}: {
  file: FileRow
  isPending: boolean
  onDetails: () => void
}) {
  const Icon = FILE_ICONS[file.extension] ?? DEFAULT_FILE_ICON
  return (
    <li className="flex flex-col gap-2 py-2 lg:flex-row lg:items-center lg:gap-3">
      <span className={cn(SLOT_COLUMNS.document, 'flex min-w-0 items-center gap-2 lg:pl-12')}>
        <Icon aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
        <FileLink href={file.href} name={file.name} />
      </span>
      <div className="flex items-center gap-3 lg:contents">
        <span className={SLOT_COLUMNS.status}>
          <SlotStatusPill status={file.status} label={fileLabel(file)} />
        </span>
        <span className={cn(SLOT_COLUMNS.expires, 'text-muted-foreground text-sm')}>
          {isPending ? (
            <span className="flex items-center gap-2">
              <Spinner className="size-3.5" />
              Reading…
            </span>
          ) : file.dateExpiration ? (
            formatDate(file.dateExpiration)
          ) : null}
        </span>
      </div>
      <span className={cn(SLOT_COLUMNS.actions, 'flex lg:justify-end')}>
        <Button variant="outline" size="xs" onClick={onDetails} disabled={isPending}>
          Details
        </Button>
      </span>
    </li>
  )
}

export function SlotRow({
  slot,
  openDetails,
  upload,
  uploadingKey,
  pickRequest,
}: { slot: ComplianceSlot } & SlotRowActions) {
  const newest: SlotDocument | undefined = slot.documents[0]
  const isEmpty = !newest
  const isPending = newest?.parseStatus === PARSE_PENDING
  const isUploading = uploadingKey === slot.key
  const style = statusStyle(slot.status)
  const inputRef = useRef<HTMLInputElement>(null)
  const modeRef = useRef<UploadMode>('add')
  const [isDragOver, setIsDragOver] = useState(false)

  const pick = (mode: UploadMode) => {
    modeRef.current = mode
    inputRef.current?.click()
  }

  useEffect(() => {
    if (pickRequest?.key === slot.key) pick('add')
  }, [pickRequest, slot.key])

  const file = (picked: File | undefined, mode: UploadMode) => {
    if (!picked) return
    const why = refusal(picked)
    if (why) {
      toast.error(why)
      return
    }
    upload(slot, picked, mode)
  }

  const onDrop = (event: DragEvent) => {
    event.preventDefault()
    setIsDragOver(false)
    if (isUploading) return
    file(event.dataTransfer.files[0], 'add')
  }

  const dropProps = {
    onDragOver: (event: DragEvent) => {
      event.preventDefault()
      setIsDragOver(true)
    },
    onDragLeave: () => setIsDragOver(false),
    onDrop,
  }

  const columns = (
    <>
      <SlotHeading slot={slot} filled={false} />
      {/* One line on a phone, two columns of the row at lg. */}
      <div className="flex items-center gap-3 lg:contents">
        <span className={SLOT_COLUMNS.status}>
          <SlotStatusPill status={slot.status} />
        </span>
        <span className={SLOT_COLUMNS.expires} />
      </div>
    </>
  )

  const input = (
    <input
      ref={inputRef}
      type="file"
      accept={SLOT_ACCEPT}
      className="hidden"
      aria-label={`Choose a file for ${slot.label}`}
      onChange={(event) => {
        file(event.target.files?.[0], modeRef.current)
        event.target.value = ''
      }}
    />
  )

  // Nothing filed: the whole row is the drop zone, and a click opens the file picker.
  if (isEmpty) {
    return (
      <>
        <button
          type="button"
          onClick={() => pick('add')}
          disabled={isUploading}
          className={cn(
            BOX,
            style.box,
            // Nothing here yet, so the card sits back — an optional one further still.
            slot.required ? 'bg-white/50' : 'bg-white/30',
            'hover:border-ink-500 hover:bg-muted/40 cursor-pointer',
            isDragOver && 'border-ink-500 bg-muted/40'
          )}
          {...dropProps}
        >
          {columns}
          <span
            className={cn(
              SLOT_COLUMNS.actions,
              'text-muted-foreground flex items-center gap-1.5 text-sm lg:justify-end'
            )}
          >
            {isUploading ? (
              <>
                <Spinner className="size-4 shrink-0" />
                Uploading…
              </>
            ) : (
              <>
                <Plus className="size-4 shrink-0" />
                {isDragOver ? 'Drop to upload' : 'Click or drop to upload'}
              </>
            )}
          </span>
        </button>
        {input}
      </>
    )
  }

  const files = fileRows(slot.documents)
  const menu = isUploading ? (
    <span className="text-muted-foreground flex items-center gap-2 text-sm">
      <Spinner className="size-4" />
      Uploading…
    </span>
  ) : (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`Upload to ${slot.label}`}>
          <Plus className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => pick('replace')}>Replace</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => pick('add')}>Add another</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  // One file reads as one line; several get a heading and a row each.
  if (files.length === 1) {
    const [file] = files
    return (
      <>
        <div className={cn(BOX, style.box, isDragOver && 'border-ink-500 bg-muted/40')} {...dropProps}>
          <SlotHeading slot={slot} filled icon={FILE_ICONS[file.extension] ?? DEFAULT_FILE_ICON}>
            <FileLink href={file.href} name={file.name} />
          </SlotHeading>
          <div className="flex items-center gap-3 lg:contents">
            <span className={SLOT_COLUMNS.status}>
              <SlotStatusPill status={file.status} label={fileLabel(file)} />
            </span>
            <span className={cn(SLOT_COLUMNS.expires, 'text-muted-foreground text-sm')}>
              {isPending ? (
                <span className="flex items-center gap-2">
                  <Spinner className="size-3.5" />
                  Reading…
                </span>
              ) : file.dateExpiration ? (
                formatDate(file.dateExpiration)
              ) : null}
            </span>
          </div>
          <span className={cn(SLOT_COLUMNS.actions, 'flex items-center gap-1 lg:justify-end')}>
            {!isUploading && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => openDetails(file.document, file.key)}
                disabled={isPending}
              >
                Details
              </Button>
            )}
            {menu}
          </span>
        </div>
        {input}
      </>
    )
  }

  // The card wears the icon of the last upload: the newest document's newest file.
  const onNewest = files.filter((file) => file.document === files[0].document)
  const latest = onNewest[onNewest.length - 1]
  return (
    <>
      <div
        className={cn(
          'flex w-full flex-col gap-1 rounded-lg border bg-white px-4 py-3 text-left transition-colors',
          style.box,
          isDragOver && 'border-ink-500 bg-muted/40'
        )}
        {...dropProps}
      >
        <div className="flex items-center gap-3">
          <SlotHeading slot={slot} filled icon={FILE_ICONS[latest.extension] ?? DEFAULT_FILE_ICON} />
          <span
            className={cn(SLOT_COLUMNS.actions, 'ml-auto flex shrink-0 items-center gap-1 lg:justify-end')}
          >
            {menu}
          </span>
        </div>
        <ul className="divide-ink-200 flex flex-col divide-y divide-dashed">
          {files.map((file) => (
            <AttachmentRow
              key={file.key}
              file={file}
              isPending={isPending}
              onDetails={() => openDetails(file.document, file.key)}
            />
          ))}
        </ul>
      </div>
      {input}
    </>
  )
}
